import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Button, Grid } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../../../../components/CustomSelect";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
// Specialty field removed from template creation
// import MaskMultiSelect from "../../../../../components/MaskMultiSelect";
import { ScrollArea } from "../../../../../components/ScrollBar";
import CustomTitle from "../../../../../components/app/CustomTitle";
import { appPaths } from "../../../../../constants/paths";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useNewTemplateCreate } from "../../../../../queries/template/new-flow/use-new-template-create";
import { useNewTemplateUpdate } from "../../../../../queries/template/new-flow/use-new-template-update";
// Specialty field removed from template creation
// import { useSpecialtiesList } from "../../../../../queries/use-specialities-list";
import {
  RosTemplate,
  RosTemplateTemplateTypesEnum,
} from "../../../../../services";
import {
  showToastOnApiCall,
  getUnNecessaryTransformData,
  getXtenantId,
} from "../../../../../utils/utils";
import { TEMPLATE_TYPE_LIST } from "../form-builder/form-builder-constant";
import EmailForm from "./EmailForm";
import SmsForm from "./SmsForm";
import {
  TEMPLATE_FORM_INITIAL_VALUES,
  TEMPLATE_VALIDATION_SCHEMA,
  newPeTemplateInitialData,
  newRosTemplateInitialData,
} from "./template-constant";
import Micro from "./template-forms/Micro";
import PeRosForm from "./template-forms/pe-template/PeRosForm";
import CarePlanForm from "./template-forms/care-plan/CarePlanForm";
import { useRoleController } from "../../../../../components/useRoleController";
import { getViewAs } from "../../../../../utils/auth-storage";
// Specialty field removed from template creation
// import { useDebounce } from "../../../../../hooks/use-debounce";

interface FormBuilderHostProps {
  isLoading?: boolean;
  preFillSchema?: any;
  enableOnlyAddApi?: boolean;
  onClose?: () => void;
}

/**
 * @author [Rajkumar Rathod]
 */
const TemplateNewForm = ({
  isLoading,
  preFillSchema,
  enableOnlyAddApi,
  onClose,
}: FormBuilderHostProps) => {
  const formik = useFormik<RosTemplate>({
    initialValues: preFillSchema || TEMPLATE_FORM_INITIAL_VALUES,
    validationSchema: TEMPLATE_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const isProvider = getViewAs() === "provider";

  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      !preFillSchema &&
      (formik.values?.templateTypes === "PHYSICAL" ||
        formik.values?.templateTypes === "ROS")
    ) {
      formik.setValues((prev) => ({
        ...prev,
        rosTemplateTypeSet: (formik.values?.templateTypes === "PHYSICAL"
          ? newPeTemplateInitialData
          : newRosTemplateInitialData) as any,
      }));
    }
  }, [formik.values?.templateTypes]);

  const { mutateAsync: createTemplate } = useNewTemplateCreate();
  const { mutateAsync: updateTemplate } = useNewTemplateUpdate();

  function handleSubmit(values: RosTemplate) {
    const transformedPayload = {
      rosTemplate: getUnNecessaryTransformData(
        ["speciality", "icdCodes"],
        values
      ),
      xTENANTID: getXtenantId(),
    };
    if (enableOnlyAddApi) {
      showToastAndResetForm(createTemplate(transformedPayload));
      return;
    }

    if (!preFillSchema)
      showToastAndResetForm(createTemplate(transformedPayload));
    else showToastAndResetForm(updateTemplate(transformedPayload));
  }

  function handleNavigateBack() {
    if (!enableOnlyAddApi) {
      navigate(
        isProvider
          ? "/app/provider/library/templates"
          : appPaths.libraryTemplatesPath
      );
    }
    onClose?.();
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      showSnackbar,
      apiCall,
      onSave: () => formik.resetForm(),
      onDelay: handleNavigateBack,
    });
  }

  const ActiveFormMapper: Record<RosTemplateTemplateTypesEnum, ReactNode> = {
    EMAIL: <EmailForm formik={formik} />,
    SMS: <SmsForm formik={formik} />,
    PHYSICAL: <PeRosForm formik={formik} />,
    MACROS: <Micro formik={formik} />,
    ROS: <PeRosForm formik={formik} />,
    CARE_PLAN: <CarePlanForm formik={formik} />,
  };

  return (
    <Flex
      flexDirection={"column"}
      width={"100%"}
      height={"calc(100vh - 100px)"}
      justifyContent={"space-between"}
    >
      <Flex flexDirection={"column"} gap={1}>
        <LoadingOverlay isLoading={!!isLoading} />
        {!enableOnlyAddApi && (
          <TemplateTitle handleNavigateBack={handleNavigateBack} />
        )}

        <TemplateBasicReqFields prefillData={preFillSchema} formik={formik} />

        <ScrollArea
          width={"100%"}
          height={`calc(100vh - ${formik.isValid ? "250px" : "320px"})`}
        >
          {/* all active form on basis of type */}
          {formik.values?.templateTypes ? (
            ActiveFormMapper[formik.values?.templateTypes]
          ) : (
            <></>
          )}
        </ScrollArea>
      </Flex>

      <Flex justifyContent={"end"} mt={1}>
        <Button variant="contained" onClick={formik.submitForm}>
          Save
        </Button>
      </Flex>
    </Flex>
  );
};

export default TemplateNewForm;

interface TemplateBasicReqFieldsProps {
  formik: FormikProps<RosTemplate>;
  prefillData?: any;
  readOnly?: boolean;
}

export const TemplateBasicReqFields = ({
  formik,
  prefillData,
  readOnly = false,
}: TemplateBasicReqFieldsProps) => {
  // Specialty field removed from template creation
  // const [searchString, setSearchString] = useDebounce<string>("");

  // const { data: specialtiesList, isLoading: isSpecialtiesListLoading } =
  //   useSpecialtiesList({
  //     searchString: searchString,
  //   });

  const { hasUpdate: hasPeUpdate } = useRoleController(
    "Templates > Physical Exam (PE)"
  );
  const { hasUpdate: hasRosUpdate } = useRoleController(
    "Templates > Review of System (ROS)"
  );

  const viewAs = getViewAs();

  // Specialty field removed from template creation
  // const templateType = formik.values?.templateTypes;
  // const isSpecialtyDisable =
  //   templateType === "MACROS" ||
  //   templateType === "EMAIL" ||
  //   templateType === "SMS" ||
  //   templateType === "PHYSICAL" ||
  //   templateType === "ROS";

  return (
    <Grid
      container
      spacing={2}
      sx={{
        mb: 1,
      }}
    >
      <Grid size={2.5}>
        <CustomSelect
          label="Template Type"
          placeholder="Select Form Type"
          disableSortOptions
          options={TEMPLATE_TYPE_LIST.filter((item) => {
            if (item.value === "PHYSICAL" && viewAs === "provider")
              return hasPeUpdate;
            if (item.value === "ROS" && viewAs === "provider")
              return hasRosUpdate;
            return true;
          })}
          value={formik.values?.templateTypes}
          readOnly={readOnly || prefillData}
          onChange={(val) => formik.setFieldValue("templateTypes", val)}
          error={formik.touched?.templateTypes && formik.errors?.templateTypes}
        />
      </Grid>

      {/* Specialty field removed from template creation */}
      {/* {!isSpecialtyDisable && (
        <Grid item xs={3.5}>
          <MaskMultiSelect
            disabled={readOnly}
            loading={isSpecialtiesListLoading}
            placeholder={isSpecialtyDisable ? "Not applicable" : "Select"}
            label="Specialty"
            name="speciality"
            onSearch={setSearchString}
            value={formik.values?.speciality as any}
            onChange={(val) => {
              formik.setFieldValue("speciality", val);
            }}
            options={specialtiesList?.data?.content?.map((specialty) => ({
              value: specialty?.uuid,
              label: specialty?.name,
            }))}
            error={
              formik.touched?.speciality && (formik.errors?.speciality as any)
            }
          />
        </Grid>
      )} */}
      <Grid size={6}>
        <Input
          label="Name Of Template"
          required
          textFieldProps={{
            disabled: readOnly,
            name: "templateName",
            value: formik.values.templateName,
            onChange: formik.handleChange,
            placeholder: "Enter Template Name",
          }}
          error={formik.touched?.templateName && formik.errors?.templateName}
        />
      </Grid>
    </Grid>
  );
};

const TemplateTitle = ({
  handleNavigateBack,
}: {
  handleNavigateBack: () => void;
}) => (
  <Flex gap={0.5} alignItems={"center"} mb={1.2}>
    <ArrowBackIcon
      sx={{ cursor: "pointer" }}
      onClick={handleNavigateBack}
      fontSize="medium"
    />
    <CustomTitle
      sx={{ cursor: "pointer" }}
      onClick={handleNavigateBack}
      color={"black"}
    >
      Template
    </CustomTitle>
  </Flex>
);
